/**
 * AtlasHub — the TWO-STEP scope + difficulty picker at `/atlas`.
 *
 * The entry to the travel study. v3 composes step 1 as ONE screenful on
 * landscape (touch-TV contract, docs/TOUCH-TV-CONTRACT.md):
 *
 *   Step 1 — "Where?"  A two-pane spread: the headline journeys (Around the
 *   World · Landmarks) on the left rail, and a tabbed "traveler's index" on
 *   the right — Continents / Countries / US States — stepped paging via
 *   tabs, never a free scroll. All 50 states live in the States tab.
 *
 *   Step 2 — "How challenging?"  Classic · Explorer · Adventurer (default
 *   Classic).
 *
 * Landmarks SKIPS step 2 (the curated set has no difficulty) and lands straight
 * on gameplay. The other scopes pick a region, then a tier; each choice is gated
 * on a real pool (`scopeIsOfferable` / `difficultiesForScope`) so we never offer
 * an empty round. The numeric difficulty score is NEVER shown — only the warm
 * tier label + a one-line plain-English subtitle.
 *
 * The page background is the Atlas-study atmosphere image (late-morning sun /
 * lamplight); the cards are clean surfaces with warm multi-layer shadows on top
 * (rule 3). A graceful CSS fallback paints a warm desk gradient if the image is
 * absent.
 */
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { AtmosphereSurface, buildSrcSet, variantOf } from '@/juice/RevealedImage';
import { usePreferences } from '@/preferences';

import { locationsForScope } from './pack';
import type { Difficulty, Scope } from './schema';
import {
  allStatesForPicker,
  allTiersForScope,
  countryName,
  defaultDifficultyForScope,
  landmarksOfferable,
  offerableContinents,
  offerableCountries,
  scopeLabel,
  worldOfferable,
} from './scopes';
import { cardSpring, HUB_STAGGER_S, hoverSpring, pageSpring } from './motion';
import { encodeScope } from './scopeParam';
import { sound } from './sound';

import './AtlasHub.css';

const BG_LIGHT = '/generated/atlas/study-light.png';
const BG_DARK = '/generated/atlas/study-dark.png';

/** Which screen of the picker is showing. */
type View = 'where' | 'challenge';

/** Which page of the traveler's index is open. */
type PlaceTab = 'continents' | 'countries' | 'states';

/**
 * What a finished choice hands back: the scope + (for regional scopes) the
 * difficulty. The route-mounted hub maps this to a `/play/atlas?...` navigation;
 * the embedded module (`/play/atlas` with no scope) maps it to an in-place mount
 * of the game — so the picker is identical in both entry points.
 */
export type AtlasChoice = (scope: Scope, difficulty?: Difficulty) => void;

/**
 * The route-mounted hub at `/atlas`. A thin wrapper around {@link AtlasPicker}
 * that turns a choice into a `/play/atlas` deep-link navigation.
 */
export function AtlasHub(): React.JSX.Element {
  const navigate = useNavigate();

  const onChoose: AtlasChoice = (scope, difficulty) => {
    const level = difficulty ? `&level=${difficulty}` : '';
    navigate(`/play/atlas?mode=daily&scope=${encodeScope(scope)}${level}`);
  };

  return <AtlasPicker onChoose={onChoose} />;
}

/**
 * The TWO-STEP scope + difficulty picker — the entry to the travel study,
 * presentation-only. It NEVER routes itself; it hands a finished choice to
 * `onChoose`. This is what both `/atlas` (route) and `/play/atlas` (embedded
 * module, hub-first) render, so the player always meets the full picker first
 * and every mode is reachable from either entry point.
 */
export function AtlasPicker({ onChoose }: { onChoose: AtlasChoice }): React.JSX.Element {
  const { theme } = usePreferences();

  const [view, setView] = useState<View>('where');
  /** The region chosen in step 1, awaiting a difficulty in step 2. */
  const [pendingScope, setPendingScope] = useState<Scope | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => {
      sound.unlock();
      sound.play('pageTurn');
    }, 80);
    return () => window.clearTimeout(t);
  }, []);

  // Each step starts from the top — relevant on phone portrait, where the
  // page may scroll (landscape is a single locked screenful).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [view]);

  /** Launch gameplay for a scope at an (optional) difficulty. */
  const play = (scope: Scope, difficulty?: Difficulty): void => {
    sound.unlock();
    sound.play('pageTurn');
    onChoose(scope, difficulty);
  };

  /** A region scope was chosen in step 1 → go to the difficulty step. */
  const chooseRegion = (scope: Scope): void => {
    sound.unlock();
    sound.play('pageTurn');
    setPendingScope(scope);
    setView('challenge');
  };

  /** Landmarks skips step 2 entirely. */
  const chooseLandmarks = (): void => play({ kind: 'landmarks' });

  const backToWhere = (): void => {
    sound.play('pageTurn');
    setPendingScope(null);
    setView('where');
  };

  const continents = useMemo(() => offerableContinents(), []);
  const countries = useMemo(() => offerableCountries(), []);
  const states = useMemo(() => allStatesForPicker(), []);

  const bg = theme === 'dark' ? BG_DARK : BG_LIGHT;

  return (
    <div className="atlas-hub" data-theme={theme} data-view={view}>
      <div className="atlas-hub__field" aria-hidden="true">
        <AtmosphereSurface
          src={bg}
          avifSrc={variantOf(bg, 'avif')}
          webpSrc={variantOf(bg, 'webp')}
          avifSrcSet={buildSrcSet(bg, 'avif', [430, 860, 1366])}
          webpSrcSet={buildSrcSet(bg, 'webp', [430, 860, 1366])}
          sizes="100vw"
          eager
        />
        <div className="atlas-hub__field-scrim" />
      </div>

      <div className="atlas-hub__inner">
        <AnimatePresence mode="wait" initial={false}>
          {view === 'where' && (
            <WhereStep
              key="where"
              continents={continents}
              countries={countries}
              states={states}
              onWorld={() => chooseRegion({ kind: 'world' })}
              onLandmarks={chooseLandmarks}
              onContinent={(c) => chooseRegion({ kind: 'continent', continent: c })}
              onCountry={(code) => chooseRegion({ kind: 'country', countryCode: code })}
              onState={(code) => chooseRegion({ kind: 'usState', stateCode: code })}
            />
          )}

          {view === 'challenge' && pendingScope && (
            <ChallengeStep
              key="challenge"
              scope={pendingScope}
              onPlay={(d) => play(pendingScope, d)}
              onBack={backToWhere}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Where? (headline journeys + the tabbed traveler's index)
// ---------------------------------------------------------------------------

const PLACE_TABS: ReadonlyArray<{ slug: PlaceTab; label: string }> = [
  { slug: 'continents', label: 'Continents' },
  { slug: 'countries', label: 'Countries' },
  { slug: 'states', label: 'US States' },
];

function WhereStep({
  continents,
  countries,
  states,
  onWorld,
  onLandmarks,
  onContinent,
  onCountry,
  onState,
}: {
  continents: ReturnType<typeof offerableContinents>;
  countries: string[];
  states: ReturnType<typeof allStatesForPicker>;
  onWorld: () => void;
  onLandmarks: () => void;
  onContinent: (c: ReturnType<typeof offerableContinents>[number]) => void;
  onCountry: (code: string) => void;
  onState: (code: string) => void;
}): React.JSX.Element {
  const [tab, setTab] = useState<PlaceTab>('continents');

  const pickTab = (next: PlaceTab): void => {
    if (next === tab) return;
    sound.unlock();
    sound.play('tick');
    setTab(next);
  };

  return (
    <motion.div
      className="atlas-hub__step atlas-hub__step--where"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={pageSpring}
    >
      {/* Left rail — the invitation + the two headline journeys. */}
      <div className="atlas-hub__rail">
        <header className="atlas-hub__head">
          <p className="atlas-hub__eyebrow">Rendever Atlas · Step 1 of 2</p>
          <h1 className="atlas-hub__title">Where would you like to go?</h1>
          <p className="atlas-hub__lede">
            See a real place, look around, and guess where you are. No clock,
            no score to chase — and each day brings a new trip, the same one
            for everyone.
          </p>
        </header>

        <section className="atlas-hub__headline" aria-label="Ways to play">
          {worldOfferable() && (
            <HeadlineCard
              index={0}
              eyebrow="Anywhere on Earth"
              title="Around the World"
              blurb="A short journey to three places, somewhere on Earth. The same trip for everyone today."
              count={locationsForScope({ kind: 'world' }, 'classic').length}
              onClick={onWorld}
            />
          )}
          {landmarksOfferable() && (
            <HeadlineCard
              index={1}
              eyebrow="Sights worth seeing"
              title="Landmarks"
              blurb="Towers, castles, temples, parks and wonders — from the Eiffel Tower to places you've never met."
              count={locationsForScope({ kind: 'landmarks' }).length}
              onClick={onLandmarks}
            />
          )}
        </section>
      </div>

      {/* Right pane — the traveler's index. Tabs page between the lists;
       * the lists themselves never scroll on landscape. */}
      <section className="atlas-hub__places" aria-label="Pick a part of the world">
        <h2 className="atlas-hub__places-title">…or pick a part of the world</h2>
        <div className="atlas-hub__tabs" role="tablist" aria-label="Kind of place">
          {PLACE_TABS.map((t) => (
            <button
              key={t.slug}
              type="button"
              role="tab"
              id={`atlas-tab-${t.slug}`}
              aria-selected={tab === t.slug}
              aria-controls="atlas-tabpanel"
              className="atlas-hub__tab"
              data-active={tab === t.slug ? 'true' : 'false'}
              onClick={() => pickTab(t.slug)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div
          id="atlas-tabpanel"
          className="atlas-hub__tabpanel"
          role="tabpanel"
          aria-labelledby={`atlas-tab-${tab}`}
        >
          <AnimatePresence mode="wait" initial={false}>
            {tab === 'continents' && (
              <motion.ul
                key="continents"
                className="atlas-hub__index atlas-hub__index--continents"
                role="list"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
              >
                {continents.map((c) => (
                  <li key={c}>
                    <button
                      type="button"
                      className="atlas-hub__place atlas-hub__place--continent"
                      onClick={() => onContinent(c)}
                      onPointerEnter={() => sound.play('tick')}
                    >
                      <span className="atlas-hub__place-name">{c}</span>
                      <span className="atlas-hub__place-count">
                        {locationsForScope({ kind: 'continent', continent: c }).length} places
                      </span>
                    </button>
                  </li>
                ))}
              </motion.ul>
            )}

            {tab === 'countries' && (
              <motion.ul
                key="countries"
                className="atlas-hub__index atlas-hub__index--countries"
                role="list"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
              >
                {countries.map((code) => (
                  <li key={code}>
                    <button
                      type="button"
                      className="atlas-hub__place"
                      onClick={() => onCountry(code)}
                      onPointerEnter={() => sound.play('tick')}
                    >
                      {countryName(code)}
                    </button>
                  </li>
                ))}
              </motion.ul>
            )}

            {tab === 'states' && (
              <motion.ul
                key="states"
                className="atlas-hub__index atlas-hub__index--states"
                role="list"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
              >
                {states.map((s) => (
                  <li key={s.code}>
                    <button
                      type="button"
                      className="atlas-hub__place"
                      onClick={() => onState(s.code)}
                      disabled={!s.offerable}
                      aria-disabled={!s.offerable}
                      title={s.offerable ? undefined : `${s.name} — coming soon`}
                      onPointerEnter={() => s.offerable && sound.play('tick')}
                    >
                      {s.name}
                    </button>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      </section>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — How challenging?
// ---------------------------------------------------------------------------

function ChallengeStep({
  scope,
  onPlay,
  onBack,
}: {
  scope: Scope;
  onPlay: (difficulty: Difficulty) => void;
  onBack: () => void;
}): React.JSX.Element {
  // All three tiers are SHOWN; the ones a thin region can't fill are dimmed.
  const tiers = useMemo(() => allTiersForScope(scope), [scope]);
  const [selected, setSelected] = useState<Difficulty>(() => defaultDifficultyForScope(scope));
  const canStart = tiers.some((t) => t.difficulty === selected && t.offerable);

  return (
    <motion.div
      className="atlas-hub__step atlas-hub__step--challenge"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={pageSpring}
    >
      <header className="atlas-hub__head">
        <button type="button" className="atlas-hub__back" onClick={onBack}>
          &larr; Back
        </button>
        <p className="atlas-hub__eyebrow">Step 2 of 2 · {scopeLabel(scope)}</p>
        <h1 className="atlas-hub__title">How challenging?</h1>
        <p className="atlas-hub__lede">
          Classic shows you towns and signs to read. The others wander further
          out, into quieter country. There&apos;s no wrong choice — pick what
          sounds nice today.
        </p>
      </header>

      <ul className="atlas-hub__tiers" role="list">
        {tiers.map((t, i) => (
          <li key={t.difficulty}>
            <TierCard
              index={i}
              label={t.label}
              subtitle={t.subtitle}
              offerable={t.offerable}
              selected={selected === t.difficulty}
              onSelect={() => {
                if (!t.offerable) return;
                sound.play('tick');
                setSelected(t.difficulty);
              }}
            />
          </li>
        ))}
      </ul>

      <div className="atlas-hub__challenge-actions">
        <button
          type="button"
          className="atlas-hub__start"
          onClick={() => onPlay(selected)}
          onPointerEnter={() => sound.play('tick')}
          disabled={!canStart}
        >
          Start in {scopeLabel(scope)} &rarr;
        </button>
      </div>
    </motion.div>
  );
}

function TierCard({
  index,
  label,
  subtitle,
  offerable,
  selected,
  onSelect,
}: {
  index: number;
  label: string;
  subtitle: string;
  offerable: boolean;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <motion.button
      type="button"
      className="atlas-hub__tier"
      data-selected={selected ? 'true' : 'false'}
      onClick={onSelect}
      disabled={!offerable}
      aria-disabled={!offerable}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...cardSpring, delay: 0.06 + index * HUB_STAGGER_S }}
      aria-pressed={selected}
    >
      <span className="atlas-hub__tier-check" aria-hidden="true" data-on={selected ? 'true' : 'false'}>
        {selected ? '✓' : ''}
      </span>
      <span className="atlas-hub__tier-label">{label}</span>
      <span className="atlas-hub__tier-sub">{subtitle}</span>
      {!offerable && <span className="atlas-hub__tier-note">Coming soon</span>}
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// Headline card
// ---------------------------------------------------------------------------

function HeadlineCard({
  index,
  eyebrow,
  title,
  blurb,
  count,
  onClick,
}: {
  index: number;
  eyebrow: string;
  title: string;
  blurb: string;
  count: number;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <motion.div
      className="atlas-hub__card-item"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...cardSpring, delay: 0.16 + index * HUB_STAGGER_S }}
      whileHover={{ y: -4, transition: hoverSpring }}
    >
      <button
        type="button"
        className="atlas-hub__card"
        onClick={onClick}
        onPointerEnter={() => sound.play('tick')}
      >
        <span className="atlas-hub__card-eyebrow">{eyebrow}</span>
        <span className="atlas-hub__card-title">{title}</span>
        <span className="atlas-hub__card-blurb">{blurb}</span>
        <span className="atlas-hub__card-foot">
          <span className="atlas-hub__card-count">{count} places</span>
          <span className="atlas-hub__card-cta" aria-hidden="true">
            Choose &rarr;
          </span>
        </span>
      </button>
    </motion.div>
  );
}
